import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAddCouponsByContract, useAddCoupons, useCheckQuota } from '../../../hooks/useCoupons';
import { useLastScoreCoefficient } from '../../../hooks/useScoreCoefficient';
import { useUserLogged } from '../../../hooks/useUserLogged';
import {
  calculateInterest,
  validateNumericInput,
  getErrorMessage,
  getPaymentMethodCode,
} from '../utils/generateCouponsUtils';
import { INITIAL_DUE_DATES, SPECIAL_PAYMENT_METHODS } from '../constants/generateCouponsConstants';

export const useGenerateCoupons = (paymentMethods = []) => {
  const navigate = useNavigate();
  const { uuid } = useUserLogged();

  const addCouponsByContractMutation = useAddCouponsByContract();
  const addCouponsMutation = useAddCoupons();
  const checkQuotaMutation = useCheckQuota();

  const { data: lastCoefficient, isLoading: coefficientLoading } = useLastScoreCoefficient();

  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [excludedContracts, setExcludedContracts] = useState([]);
  const [noCalculateInterest, setNoCalculateInterest] = useState(false);

  const [formData, setFormData] = useState({
    branch: '',
    plan: '',
    group: 'all',
    paymentMethod: '',
    contract: '',
    defermentPercentage: 0,
    coefficient: 0,
  });

  const [dueDates, setDueDates] = useState(INITIAL_DUE_DATES.amounts);
  const [dueDatesCalendar, setDueDatesCalendar] = useState(INITIAL_DUE_DATES.dates);
  const [diffValues, setDiffValues] = useState(INITIAL_DUE_DATES.differences);

  useEffect(() => {
    if (lastCoefficient && !coefficientLoading) {
      setFormData((prev) => ({
        ...prev,
        coefficient: lastCoefficient.value,
      }));
    }
  }, [lastCoefficient, coefficientLoading]);

  const isSpecialPaymentMethod = useCallback((paymentMethodCode) => {
    return SPECIAL_PAYMENT_METHODS.includes(paymentMethodCode);
  }, []);

  const calculateDueDates = useCallback((principal, dates, noInterest = false) => {
    if (noInterest) {
      return {
        due1: principal,
        due2: principal,
        due3: principal,
      };
    }

    const interest2 = calculateInterest(principal, dates.due1, dates.due2);
    const interest3 = calculateInterest(principal, dates.due1, dates.due3);

    return {
      due1: principal,
      due2: parseFloat(interest2.total),
      due3: parseFloat(interest3.total),
    };
  }, []);

  const calculateDifferenceValues = useCallback((amounts, percentage) => {
    return {
      diff1: amounts.due1 - (amounts.due1 * percentage) / 100,
      diff2: amounts.due2 - (amounts.due2 * percentage) / 100,
      diff3: amounts.due3 - (amounts.due3 * percentage) / 100,
    };
  }, []);

  const handleFormChange = useCallback(
    (name, value) => {
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          [name]: value,
        };

        if (name === 'defermentPercentage') {
          const newDiffValues = calculateDifferenceValues(dueDates, parseFloat(value) || 0);
          setDiffValues(newDiffValues);
        }

        return newFormData;
      });
    },
    [dueDates, calculateDifferenceValues]
  );

  const handleDueDateChange = useCallback(
    (value, dueNumber) => {
      if (!validateNumericInput(value)) return;

      let newDueDates = { ...dueDates };

      if (dueNumber === 'due1') {
        const calculatedDates = calculateDueDates(
          parseFloat(value),
          dueDatesCalendar,
          noCalculateInterest
        );
        newDueDates = calculatedDates;
      } else {
        newDueDates[dueNumber] = parseFloat(value);
      }

      setDueDates(newDueDates);

      const newDiffValues = calculateDifferenceValues(newDueDates, formData.defermentPercentage);
      setDiffValues(newDiffValues);
    },
    [
      dueDates,
      dueDatesCalendar,
      noCalculateInterest,
      formData.defermentPercentage,
      calculateDueDates,
      calculateDifferenceValues,
    ]
  );

  const handleDateCalendarChange = useCallback(
    (value, dueNumber) => {
      const newDate = new Date(value);
      const newDueDatesCalendar = {
        ...dueDatesCalendar,
        [dueNumber]: newDate,
      };

      setDueDatesCalendar(newDueDatesCalendar);

      if (!noCalculateInterest) {
        const calculatedDates = calculateDueDates(dueDates.due1, newDueDatesCalendar, false);
        setDueDates(calculatedDates);

        const newDiffValues = calculateDifferenceValues(
          calculatedDates,
          formData.defermentPercentage
        );
        setDiffValues(newDiffValues);
      }
    },
    [
      dueDatesCalendar,
      dueDates.due1,
      noCalculateInterest,
      formData.defermentPercentage,
      calculateDueDates,
      calculateDifferenceValues,
    ]
  );

  const handleInterestCheckboxChange = useCallback(
    (checked) => {
      setNoCalculateInterest(checked);

      if (checked) {
        const newDueDates = {
          due1: dueDates.due1,
          due2: dueDates.due1,
          due3: dueDates.due1,
        };
        setDueDates(newDueDates);

        const newDiffValues = calculateDifferenceValues(newDueDates, formData.defermentPercentage);
        setDiffValues(newDiffValues);
      } else {
        const calculatedDates = calculateDueDates(dueDates.due1, dueDatesCalendar, false);
        setDueDates(calculatedDates);

        const newDiffValues = calculateDifferenceValues(
          calculatedDates,
          formData.defermentPercentage
        );
        setDiffValues(newDiffValues);
      }
    },
    [
      dueDates.due1,
      dueDatesCalendar,
      formData.defermentPercentage,
      calculateDueDates,
      calculateDifferenceValues,
    ]
  );

  const handleExcludedContractsChange = useCallback((contractUuids) => {
    setExcludedContracts(contractUuids);
  }, []);

  const handleGenerateClick = useCallback(async () => {
    setIsLoading(true);
    try {
      const paymentMethodCode = getPaymentMethodCode(paymentMethods, formData.paymentMethod);
      const specialCodes = ['PU0000', 'AD0000', 'ADJ000'];
      if (specialCodes.includes(paymentMethodCode)) {
        setPopupData({
          message: 'Toque confirmar para generar el cupón.',
          contracts: [],
          confirm: true,
          navigate: false,
        });
        setExcludedContracts([]);
        setShowPopup(true);
        setIsLoading(false);
        return;
      }

      const checkData = {
        uuid_payment_method: formData.paymentMethod,
        uuid_group: formData.group,
        uuid_plan: formData.plan,
        contract_number: formData.contract || null,
        month: dueDatesCalendar.due1.getMonth() + 1,
        year: dueDatesCalendar.due1.getFullYear(),
      };

      const result = await checkQuotaMutation.mutateAsync(checkData);

      const message =
        result && result.length > 0
          ? 'Tildar el contrato para no generar cupón:'
          : 'Toque confirmar para generar los cupones.';

      setPopupData({
        message,
        contracts: result,
        confirm: result[0]?.contrato ? true : false,
        navigate: false,
      });

      setExcludedContracts([]);
      setShowPopup(true);
    } catch (error) {
      console.error('Error checking quota:', error);
      setPopupData({
        message: 'Error al verificar cuota. Intente nuevamente.',
        confirm: false,
        navigate: false,
      });
      setShowPopup(true);
    } finally {
      setIsLoading(false);
    }
  }, [formData, checkQuotaMutation, dueDatesCalendar.due1, paymentMethods]);

  const handleConfirmGeneration = useCallback(async () => {
    setIsLoading(true);
    try {
      const couponData = {
        uuid_plan: formData.plan,
        uuid_group: formData.group,
        uuid_payment_method: formData.paymentMethod,
        numberContract: formData.contract || '',
        uuid_user_gen: uuid,
        defermentPercentage: formData.defermentPercentage,
        coefficient: formData.coefficient,
        first_due_date: dueDatesCalendar.due1.toISOString().split('T')[0],
        second_due_date: dueDatesCalendar.due2.toISOString().split('T')[0],
        third_due_date: dueDatesCalendar.due3.toISOString().split('T')[0],
        first_due_amount: String(dueDates.due1),
        second_due_amount: String(dueDates.due2),
        third_due_amount: String(dueDates.due3),
        excluded_contracts: excludedContracts,
      };

      let result;
      if (formData.contract) {
        if (excludedContracts.length > 0) {
          setPopupData({
            message: 'Generación de cupón cancelada con éxito',
            confirm: false,
            navigate: false,
          });
          setShowPopup(true);
          return;
        }
        result = await addCouponsByContractMutation.mutateAsync(couponData);
      } else {
        result = await addCouponsMutation.mutateAsync(couponData);
      }

      if (result.status === 201) {
        const paymentMethodCode = getPaymentMethodCode(paymentMethods, formData.paymentMethod);
        const specialCodes = ['PU0000', 'AD0000', 'ADJ000'];
        const successMessage = specialCodes.includes(paymentMethodCode)
          ? 'Cupón generado correctamente.'
          : '¡Cupón generado con éxito! Ya puede descargar el cupón.';

        setPopupData({
          message: successMessage,
          confirm: false,
          navigate: true,
        });
        setShowPopup(true);
      }
    } catch (error) {
      console.error('Full error object:', error);

      let errorMessage = 'Error inesperado';

      if (error?.response?.data) {
        errorMessage = `Error de validación: ${error.response.data.error}`;
      } else {
        errorMessage = getErrorMessage(error);
      }

      setPopupData({
        message: errorMessage,
        confirm: false,
        navigate: false,
      });
      setShowPopup(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    uuid,
    formData,
    dueDates,
    dueDatesCalendar,
    excludedContracts,
    addCouponsByContractMutation,
    addCouponsMutation,
    paymentMethods,
  ]);

  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
    setPopupData(null);
  }, []);

  const handleNavigateToCoupons = useCallback(() => {
    navigate('/cupon/cupones/');
  }, [navigate]);

  return {
    isLoading,
    showPopup,
    popupData,
    excludedContracts,
    noCalculateInterest,
    formData,
    dueDates,
    dueDatesCalendar,
    diffValues,
    coefficientLoading,
    handleFormChange,
    handleDueDateChange,
    handleDateCalendarChange,
    handleInterestCheckboxChange,
    handleExcludedContractsChange,
    handleGenerateClick,
    handleConfirmGeneration,
    handleClosePopup,
    handleNavigateToCoupons,
    isSpecialPaymentMethod,
  };
};
